import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  Table,
  Drawer,
  BadgePill,
  EmptyState,
} from '@/components/campus';
import {
  FileText,
  Upload,
  Loader2,
  Eye,
  Trash2,
  Download,
  PenTool,
  Type,
  Check,
  Clock,
  Users,
  MoreHorizontal,
  CheckCircle,
  FileCode,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Document type options
const DOC_TYPES = [
  { value: 'bylaws', label: 'Bylaws' },
  { value: 'constitution', label: 'Constitution' },
  { value: 'policy', label: 'Policy Document' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'waiver', label: 'Waiver' },
  { value: 'form', label: 'Form' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active Members' },
  { value: 'pledge', label: 'Pledges' },
  { value: 'alumni', label: 'Alumni' },
];

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ==================== SIGNATURE PAD COMPONENT ====================
const SignaturePad = ({ onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('draw');
  const [typedName, setTypedName] = useState('');
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSave = () => {
    if (activeTab === 'draw') {
      if (!hasDrawn) {
        toast.error('Please draw your signature');
        return;
      }
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl, 'drawn');
    } else {
      if (!typedName.trim()) {
        toast.error('Please type your name');
        return;
      }
      // Create canvas with typed signature
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#1a1a2e';
      ctx.font = 'italic 48px "Brush Script MT", cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
      const dataUrl = canvas.toDataURL('image/png');
      onSave(dataUrl, 'typed');
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full bg-graphite/50">
          <TabsTrigger value="draw" className="flex-1 data-[state=active]:bg-electric-ice-blue/20">
            <PenTool className="w-4 h-4 mr-2" />
            Draw
          </TabsTrigger>
          <TabsTrigger value="type" className="flex-1 data-[state=active]:bg-electric-ice-blue/20">
            <Type className="w-4 h-4 mr-2" />
            Type
          </TabsTrigger>
        </TabsList>

        <TabsContent value="draw" className="mt-4">
          <div className="space-y-3">
            <div className="border border-white/[0.08] rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className="w-full cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearCanvas}
              className="border-white/[0.08] text-soft-silver"
            >
              Clear
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="type" className="mt-4">
          <div className="space-y-3">
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
            {typedName && (
              <div className="border border-white/[0.08] rounded-lg p-6 bg-white">
                <p
                  className="text-center text-3xl text-gray-800"
                  style={{ fontFamily: '"Brush Script MT", cursive', fontStyle: 'italic' }}
                >
                  {typedName}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
        >
          <Check className="w-4 h-4 mr-2" />
          Sign Document
        </Button>
      </div>
    </div>
  );
};

// ==================== UPLOAD FORM ====================
const UploadDocumentForm = ({ onSubmit, onCancel, isLoading }) => {
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('');
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [applicableStatuses, setApplicableStatuses] = useState(['active']);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const validate = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!docType) newErrors.docType = 'Document type is required';
    if (!file) newErrors.file = 'File is required';
    if (requiresSignature && applicableStatuses.length === 0) {
      newErrors.statuses = 'Select at least one status';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      title,
      doc_type: docType,
      requires_signature: requiresSignature,
      applicable_statuses: applicableStatuses,
      file,
    });
  };

  const toggleStatus = (status) => {
    setApplicableStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Title */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Document Title *</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Spring 2026 Membership Agreement"
          className="bg-obsidian border-white/[0.08] text-cloud-white"
          data-testid="doc-title-input"
        />
        {errors.title && <p className="text-signal-red text-xs">{errors.title}</p>}
      </div>

      {/* Document Type */}
      <div className="space-y-2">
        <Label className="text-soft-silver">Document Type *</Label>
        <Select value={docType} onValueChange={setDocType}>
          <SelectTrigger className="bg-obsidian border-white/[0.08] text-cloud-white">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            {DOC_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-cloud-white">
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.docType && <p className="text-signal-red text-xs">{errors.docType}</p>}
      </div>

      {/* File Upload */}
      <div className="space-y-2">
        <Label className="text-soft-silver">File *</Label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
            file ? "border-electric-ice-blue/50 bg-electric-ice-blue/5" : "border-white/[0.08] hover:border-white/[0.2]"
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            onChange={(e) => setFile(e.target.files[0])}
            className="hidden"
          />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <FileText className="w-5 h-5 text-electric-ice-blue" />
              <span className="text-cloud-white">{file.name}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="w-8 h-8 text-soft-silver mx-auto" />
              <p className="text-soft-silver text-sm">Click to upload PDF or image</p>
            </div>
          )}
        </div>
        {errors.file && <p className="text-signal-red text-xs">{errors.file}</p>}
      </div>

      {/* Requires Signature */}
      <div className="flex items-center space-x-3 p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
        <Checkbox
          checked={requiresSignature}
          onCheckedChange={setRequiresSignature}
          className="border-white/[0.2] data-[state=checked]:bg-electric-ice-blue"
        />
        <div>
          <p className="text-cloud-white font-medium">Requires Signature</p>
          <p className="text-soft-silver text-sm">Members will need to sign this document</p>
        </div>
      </div>

      {/* Applicable Statuses (if requires signature) */}
      {requiresSignature && (
        <div className="space-y-3">
          <Label className="text-soft-silver">Who needs to sign? *</Label>
          <div className="space-y-2">
            {STATUS_OPTIONS.map(opt => (
              <div
                key={opt.value}
                className="flex items-center space-x-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06] cursor-pointer hover:border-white/[0.12]"
                onClick={() => toggleStatus(opt.value)}
              >
                <Checkbox
                  checked={applicableStatuses.includes(opt.value)}
                  onCheckedChange={() => toggleStatus(opt.value)}
                  className="border-white/[0.2] data-[state=checked]:bg-electric-ice-blue"
                />
                <span className="text-cloud-white">{opt.label}</span>
              </div>
            ))}
          </div>
          {errors.statuses && <p className="text-signal-red text-xs">{errors.statuses}</p>}
        </div>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4 border-t border-white/[0.06]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1 border-white/[0.08] text-soft-silver hover:text-cloud-white"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
          disabled={isLoading}
          data-testid="upload-doc-submit-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : 'Upload Document'}
        </Button>
      </div>
    </form>
  );
};

// ==================== DOCUMENT DETAIL VIEW ====================
const DocumentDetailView = ({ document, signatures, onSign, onDelete, isAdmin, userHasSigned, isProcessing }) => {
  const [showSignature, setShowSignature] = useState(false);

  if (!document) return null;

  const stats = document.signature_stats;

  return (
    <div className="space-y-6">
      {/* Document Info */}
      <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
        <h3 className="text-lg font-semibold text-cloud-white">{document.title}</h3>
        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-soft-silver">
          <BadgePill variant="default">{DOC_TYPES.find(t => t.value === document.doc_type)?.label || document.doc_type}</BadgePill>
          <span>Uploaded by {document.uploaded_by_name}</span>
          <span>{formatDate(document.created_at)}</span>
        </div>
      </div>

      {/* View/Download Document */}
      <div className="flex gap-3">
        <Button
          onClick={() => window.open(document.file_url, '_blank')}
          variant="outline"
          className="flex-1 border-white/[0.08] text-cloud-white"
        >
          <Eye className="w-4 h-4 mr-2" />
          View Document
        </Button>
        <Button
          onClick={() => {
            const a = document.createElement('a');
            a.href = document.file_url;
            a.download = document.file_name;
            a.click();
          }}
          variant="outline"
          className="flex-1 border-white/[0.08] text-cloud-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Download
        </Button>
      </div>

      {/* Signature Stats (if requires signature) */}
      {document.requires_signature && stats && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
              <p className="text-cool-mint text-2xl font-semibold">{stats.signed_count}</p>
              <p className="text-soft-silver text-sm">Signed</p>
            </div>
            <div className="p-4 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
              <p className="text-soft-amber text-2xl font-semibold">{stats.pending_count}</p>
              <p className="text-soft-silver text-sm">Pending</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-soft-silver">Completion</span>
              <span className="text-electric-ice-blue font-medium">{stats.completion_rate}%</span>
            </div>
            <Progress value={stats.completion_rate} className="h-2 bg-graphite" />
          </div>
        </div>
      )}

      {/* Sign Button (for members who haven't signed) */}
      {document.requires_signature && !userHasSigned && !isAdmin && (
        <div className="pt-4 border-t border-white/[0.06]">
          {!showSignature ? (
            <Button
              onClick={() => setShowSignature(true)}
              className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
              data-testid="sign-document-btn"
            >
              <PenTool className="w-4 h-4 mr-2" />
              Sign This Document
            </Button>
          ) : (
            <SignaturePad
              onSave={(data, type) => {
                onSign(data, type);
                setShowSignature(false);
              }}
              onCancel={() => setShowSignature(false)}
            />
          )}
        </div>
      )}

      {/* Already Signed Badge */}
      {userHasSigned && (
        <div className="flex items-center gap-2 p-4 bg-cool-mint/10 rounded-lg border border-cool-mint/20">
          <CheckCircle className="w-5 h-5 text-cool-mint" />
          <span className="text-cool-mint font-medium">You have signed this document</span>
        </div>
      )}

      {/* Signatures List (Admin View) */}
      {isAdmin && signatures && signatures.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-soft-silver">Signatures ({signatures.length})</h4>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {signatures.map(sig => (
              <div
                key={sig.id}
                className="flex items-center justify-between p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cool-mint/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-cool-mint" />
                  </div>
                  <div>
                    <p className="text-cloud-white text-sm">{sig.signer_name}</p>
                    <p className="text-muted-text text-xs">{sig.signer_email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <BadgePill variant="success">{sig.signature_type}</BadgePill>
                  <p className="text-muted-text text-xs mt-1">{formatDate(sig.signed_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Delete Action */}
      {isAdmin && (
        <div className="pt-4 border-t border-white/[0.06]">
          <Button
            onClick={onDelete}
            variant="outline"
            className="w-full border-signal-red/50 text-signal-red hover:bg-signal-red/10"
            disabled={isProcessing}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Document
          </Button>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN DOCUMENTS PAGE ====================
export const Documents = () => {
  const { organization, session } = useAuth();
  const { hasOrgRole } = usePermissions();
  const navigate = useNavigate();
  const isAdmin = hasOrgRole(['owner', 'admin']);

  // State
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docSignatures, setDocSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!organization?.id || !session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/documents/list/${organization.id}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle URL param for direct sign
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const signDocId = urlParams.get('sign');
    if (signDocId && documents.length > 0) {
      const doc = documents.find(d => d.id === signDocId);
      if (doc) {
        handleViewDocument(doc);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [documents]);

  // Upload document
  const handleUploadDocument = async (data) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('organization_id', organization.id);
      formData.append('title', data.title);
      formData.append('doc_type', data.doc_type);
      formData.append('requires_signature', data.requires_signature);
      formData.append('applicable_statuses', data.applicable_statuses.join(','));
      formData.append('file', data.file);

      const res = await fetch(`${API_URL}/api/documents/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (res.ok) {
        toast.success('Document uploaded successfully!');
        setIsUploadOpen(false);
        fetchDocuments();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upload document');
      }
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setIsSubmitting(false);
    }
  };

  // View document detail
  const handleViewDocument = async (doc) => {
    setSelectedDoc(doc);
    setIsDetailOpen(true);

    try {
      const res = await fetch(`${API_URL}/api/documents/${doc.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setDocSignatures(data.signatures || []);
        setSelectedDoc(prev => ({
          ...prev,
          user_has_signed: data.user_has_signed,
          signature_stats: data.signature_stats
        }));
      }
    } catch (error) {
      console.error('Failed to fetch document detail:', error);
    }
  };

  // Sign document
  const handleSignDocument = async (signatureData, signatureType) => {
    if (!selectedDoc) return;
    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/documents/${selectedDoc.id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          signature_data: signatureData,
          signature_type: signatureType,
        }),
      });

      if (res.ok) {
        toast.success('Document signed successfully!');
        setSelectedDoc(prev => ({ ...prev, user_has_signed: true }));
        fetchDocuments();
        handleViewDocument(selectedDoc);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to sign document');
      }
    } catch (error) {
      toast.error('Failed to sign document');
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async () => {
    if (!selectedDoc) return;
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setIsProcessing(true);

    try {
      const res = await fetch(`${API_URL}/api/documents/${selectedDoc.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Document deleted');
        setIsDetailOpen(false);
        setSelectedDoc(null);
        fetchDocuments();
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setIsProcessing(false);
    }
  };

  // Table columns
  const columns = [
    {
      key: 'title',
      label: 'Document',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{value}</p>
            <p className="text-xs text-soft-silver">{DOC_TYPES.find(t => t.value === row.doc_type)?.label}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'requires_signature',
      label: 'Signature',
      render: (value, row) => {
        if (!value) return <span className="text-soft-silver">Not required</span>;
        const stats = row.signature_stats;
        if (!stats) return <BadgePill variant="default">Required</BadgePill>;
        return (
          <div className="flex items-center gap-2">
            <Progress value={stats.completion_rate} className="w-16 h-2 bg-graphite" />
            <span className="text-xs text-soft-silver">{stats.completion_rate}%</span>
          </div>
        );
      },
    },
    {
      key: 'user_has_signed',
      label: 'Status',
      render: (value, row) => {
        if (!row.requires_signature) return null;
        return value ? (
          <BadgePill variant="success">
            <Check className="w-3 h-3 mr-1" />
            Signed
          </BadgePill>
        ) : (
          <BadgePill variant="warning">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </BadgePill>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Uploaded',
      render: (value) => (
        <span className="text-soft-silver text-sm">{formatDate(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      sortable: false,
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleViewDocument(row)}
          className="text-electric-ice-blue hover:text-electric-ice-blue/80"
        >
          <Eye className="w-4 h-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      <PageHeader
        title="Document Vault"
        subtitle="Manage organization documents and signatures"
        actions={
          isAdmin && (
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/app/documents/templates')}
                variant="outline"
                className="border-electric-ice-blue/30 text-electric-ice-blue hover:bg-electric-ice-blue/10"
                data-testid="templates-btn"
              >
                <FileCode className="w-4 h-4 mr-2" />
                Templates
              </Button>
              <Button
                onClick={() => setIsUploadOpen(true)}
                className="bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90"
                data-testid="upload-doc-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>
          )
        }
      />

      <Table
        columns={columns}
        data={documents}
        emptyState={
          <EmptyState
            icon="file-text"
            title="No documents yet"
            description={isAdmin ? "Upload your first document to get started." : "No documents available."}
            actionLabel={isAdmin ? "Upload Document" : undefined}
            onAction={isAdmin ? () => setIsUploadOpen(true) : undefined}
          />
        }
      />

      {/* Upload Drawer */}
      <Drawer
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Document"
        width="md"
      >
        <UploadDocumentForm
          onSubmit={handleUploadDocument}
          onCancel={() => setIsUploadOpen(false)}
          isLoading={isSubmitting}
        />
      </Drawer>

      {/* Document Detail Drawer */}
      <Drawer
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedDoc(null);
          setDocSignatures([]);
        }}
        title="Document Details"
        width="lg"
      >
        <DocumentDetailView
          document={selectedDoc}
          signatures={docSignatures}
          onSign={handleSignDocument}
          onDelete={handleDeleteDocument}
          isAdmin={isAdmin}
          userHasSigned={selectedDoc?.user_has_signed}
          isProcessing={isProcessing}
        />
      </Drawer>
    </div>
  );
};

export default Documents;
